import { gql, useQuery } from '@apollo/client';
import * as immutable from 'object-path-immutable';
import uniqBy from 'lodash/uniqBy';
import TaskListSectionlessFragment from 'components/projects/taskList/gql/fragments/TaskListSectionlessFragment.graphql';
import { pageInfoFromData } from 'helpers/graphql-utils';

export const GET_TASK_LIST_SECTIONLESS = gql`
  query GetTaskListSectionless($id: ID!, $first: Int, $after: String) {
    viewer {
      __typename
      id
      taskList(id: $id) {
        __typename
        ...TaskListSectionlessFragment
      }
    }
  }
  ${TaskListSectionlessFragment}
`;

const nodes = (data) => data?.viewer.taskList.tasks.nodes ?? [];

export const SECTIONLESS_TASKS_PER_PAGE = 100;
const BASE_PATH = 'viewer.taskList.tasks';

const useGetSectionlessTasks = ({ taskListId }) => {
  // WARNING - THIS IS RECURSIVELY FETCHING ALL TASK NODES
  // There is no limit on the amount of nodes that can be fetched
  // This could result in run-away queries
  const fetchMoreTasks = async (previousResponse, fetchMore) => {
    const pageInfo = pageInfoFromData(previousResponse, BASE_PATH);

    if (pageInfo.hasNextPage) {
      const response = await fetchMore({
        variables: {
          id: taskListId,
          first: SECTIONLESS_TASKS_PER_PAGE,
          after: pageInfo.endCursor,
        },
        updateQuery: (prevData, { fetchMoreResult: newData }) => {
          if (!newData) return prevData;

          return immutable.set(
            newData,
            'viewer.taskList.tasks.nodes',
            uniqBy([...nodes(prevData), ...nodes(newData)], (x) => x.id),
          );
        },
      });
      fetchMoreTasks(response.data, fetchMore);
    }
  };

  const { data, loading, refetch, fetchMore, error } = useQuery(
    GET_TASK_LIST_SECTIONLESS,
    {
      variables: { id: taskListId, first: SECTIONLESS_TASKS_PER_PAGE },
      fetchPolicy: 'cache-and-network',
      onCompleted: (res) => {
        fetchMoreTasks(res, fetchMore);
      },
    },
  );

  return {
    sectionlessTasks: data?.viewer?.taskList?.tasks?.nodes || [],
    loading,
    refetch,
    error,
  };
};

export default useGetSectionlessTasks;
